import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  TextField,
  Chip,
  Divider,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Alert,
  Collapse,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import TranslateIcon from "@mui/icons-material/Translate";
import { toast } from "react-toastify";
import api from "../../../conf/api";

const DEFAULT_PROMPT =
  "You are a creative video metadata writer.\n\n" +
  "Rephrase the given content using noticeably different wording while preserving the exact same meaning, context, and intent.\n" +
  "The result should feel like it was written by a different person, not a light edit.\n\n" +
  "Requirements:\n" +
  "- Keep the tone, style, and emotional intent the same.\n" +
  "- Do not reuse the original phrasing or sentence structure.\n" +
  "- Maintain all brand names, product names, proper nouns, and technical terms exactly as provided.\n" +
  "- Ensure the output sounds natural, fluent, and engaging.\n" +
  "- Do not add new information or remove any meaning.\n\n" +
  '- Do not use any name if available in the description.\n\n' +
  "Output rules:\n" +
  "- Return only valid JSON.\n" +
  "- Do not include markdown, comments, or explanations.\n" +
  "- Do not include any extra text outside the JSON.";

const LANGUAGES = [
  "English",
  "Spanish",
  "French",
  "German",
  "Italian",
  "Portuguese",
  "Dutch",
  "Russian",
  "Chinese",
  "Japanese",
  "Korean",
  "Arabic",
  "Hindi",
  "Turkish",
  "Polish",
  "Swedish",
  "Norwegian",
  "Danish",
  "Finnish",
  "Greek",
  "Czech",
  "Romanian",
  "Hungarian",
  "Thai",
  "Vietnamese",
  "Indonesian",
  "Malay",
  "Filipino",
  "Hebrew",
  "Ukrainian",
];

/**
 * Step for reviewing and editing AI-rephrased content
 * Shows original vs rephrased side-by-side with edit, re-request, prompt customization, and language selection
 */
function RephrasedContentReview({
  selectedVideos = [],
  onContentApproved,
  onBack,
}) {
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [rephrasingStates, setRephrasingStates] = useState({});
  const [loading, setLoading] = useState(false);

  // Per-video prompt and language
  const [videoPrompts, setVideoPrompts] = useState({});
  const [videoLanguages, setVideoLanguages] = useState({});
  const [promptExpanded, setPromptExpanded] = useState(false);

  const currentVideo = selectedVideos[currentVideoIndex];

  useEffect(() => {
    initializeRephrasing();
  }, []);

  const initializeRephrasing = async () => {
    setLoading(true);
    const states = {};
    const prompts = {};
    const languages = {};

    for (let i = 0; i < selectedVideos.length; i++) {
      const video = selectedVideos[i];
      states[video.unique_key] = {
        original: {
          title: video.title || video.video_title,
          description: video.description || "",
          keywords: video.keywords || [],
        },
        rephrased: null,
        editing: {
          title: "",
          description: "",
          keywords: [],
        },
        isLoading: false,
        approved: false,
        error: null,
      };
      prompts[video.unique_key] = DEFAULT_PROMPT;
      languages[video.unique_key] = "English";

      if (i === 0) {
        await requestRephrase(video.unique_key, video, DEFAULT_PROMPT, "English");
      }
    }

    setRephrasingStates(states);
    setVideoPrompts(prompts);
    setVideoLanguages(languages);
    setLoading(false);
  };

  const requestRephrase = async (uniqueKey, video, customPrompt, language) => {
    const videoData =
      video || selectedVideos.find((v) => v.unique_key === uniqueKey);

    const prompt = customPrompt || videoPrompts[uniqueKey] || DEFAULT_PROMPT;
    const lang = language || videoLanguages[uniqueKey] || "English";

    setRephrasingStates((prev) => ({
      ...prev,
      [uniqueKey]: {
        ...prev[uniqueKey],
        isLoading: true,
        error: null,
      },
    }));

    try {
      const response = await api.post(
        "/mrss/rephrase/",
        {
          title: videoData.title || videoData.video_title,
          description: videoData.description || "",
          keywords: videoData.keywords || [],
          target_language: lang,
          custom_prompt: prompt !== DEFAULT_PROMPT ? prompt : undefined,
        },
        { timeout: 6 * 60 * 1000 }
      );

      if (response.data.success) {
        setRephrasingStates((prev) => ({
          ...prev,
          [uniqueKey]: {
            ...prev[uniqueKey],
            rephrased: {
              title: response.data.title,
              description: response.data.description,
              keywords: response.data.keywords,
            },
            editing: {
              title: response.data.title,
              description: response.data.description,
              keywords: response.data.keywords,
            },
            isLoading: false,
          },
        }));
        toast.success(
          lang.toLowerCase() === "english"
            ? "Content rephrased successfully!"
            : `Content translated to ${lang} successfully!`
        );
      } else {
        throw new Error(response.data.error || "Rephrasing failed");
      }
    } catch (error) {
      console.error("Rephrasing error:", error);
      setRephrasingStates((prev) => ({
        ...prev,
        [uniqueKey]: {
          ...prev[uniqueKey],
          isLoading: false,
          error:
            error.response?.data?.error || error.message || "Rephrasing failed",
          rephrased: prev[uniqueKey]?.original || null,
          editing: prev[uniqueKey]?.original || { title: "", description: "", keywords: [] },
        },
      }));
      toast.error("Rephrasing failed. Using original content.");
    }
  };

  const handleEditChange = (field, value) => {
    const uniqueKey = currentVideo.unique_key;
    setRephrasingStates((prev) => ({
      ...prev,
      [uniqueKey]: {
        ...prev[uniqueKey],
        editing: {
          ...prev[uniqueKey].editing,
          [field]: value,
        },
      },
    }));
  };

  const handlePromptChange = (uniqueKey, value) => {
    setVideoPrompts((prev) => ({ ...prev, [uniqueKey]: value }));
  };

  const handleLanguageChange = (uniqueKey, value) => {
    setVideoLanguages((prev) => ({ ...prev, [uniqueKey]: value }));
  };

  const handleResetPrompt = (uniqueKey) => {
    setVideoPrompts((prev) => ({ ...prev, [uniqueKey]: DEFAULT_PROMPT }));
  };

  const handleApprove = () => {
    const uniqueKey = currentVideo.unique_key;
    setRephrasingStates((prev) => ({
      ...prev,
      [uniqueKey]: {
        ...prev[uniqueKey],
        approved: true,
      },
    }));

    if (currentVideoIndex < selectedVideos.length - 1) {
      const nextIndex = currentVideoIndex + 1;
      setCurrentVideoIndex(nextIndex);

      const nextVideo = selectedVideos[nextIndex];
      if (!rephrasingStates[nextVideo.unique_key]?.rephrased) {
        requestRephrase(
          nextVideo.unique_key,
          nextVideo,
          videoPrompts[nextVideo.unique_key],
          videoLanguages[nextVideo.unique_key]
        );
      }
    } else {
      const approvedContent = {};
      selectedVideos.forEach((video) => {
        const state = rephrasingStates[video.unique_key];
        approvedContent[video.unique_key] = state.editing;
      });
      onContentApproved(approvedContent);
    }
  };

  const handleUseOriginal = () => {
    const uniqueKey = currentVideo.unique_key;
    setRephrasingStates((prev) => ({
      ...prev,
      [uniqueKey]: {
        ...prev[uniqueKey],
        editing: prev[uniqueKey].original,
      },
    }));
  };

  const handlePreviousVideo = () => {
    if (currentVideoIndex > 0) {
      setCurrentVideoIndex(currentVideoIndex - 1);
    }
  };

  if (loading || !currentVideo || !rephrasingStates[currentVideo.unique_key]) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={5}>
        <CircularProgress />
        <Typography variant="body1" ml={2}>
          Initializing AI SEO...
        </Typography>
      </Box>
    );
  }

  const state = rephrasingStates[currentVideo.unique_key];
  const progress = `${currentVideoIndex + 1}/${selectedVideos.length}`;
  const currentPrompt = videoPrompts[currentVideo.unique_key] || DEFAULT_PROMPT;
  const currentLanguage = videoLanguages[currentVideo.unique_key] || "English";
  const isPromptModified = currentPrompt !== DEFAULT_PROMPT;

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box>
          <Typography variant="h6" gutterBottom>
            Review AI & SEO Content
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Video {progress}: {currentVideo.title || currentVideo.video_title}
          </Typography>
        </Box>
        <Chip
          label={`${progress} Videos`}
          color="primary"
          icon={<AutoAwesomeIcon />}
        />
      </Box>

      {state.error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {state.error}. You can edit the content manually or use the original.
        </Alert>
      )}

      {/* Language Selection & Prompt Section */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box display="flex" alignItems="center" gap={2} flex={1}>
            {/* Language Selector */}
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <TranslateIcon fontSize="small" />
                  Language
                </Box>
              </InputLabel>
              <Select
                value={currentLanguage}
                label="Language"
                onChange={(e) => {
                  handleLanguageChange(currentVideo.unique_key, e.target.value);
                }}
              >
                {LANGUAGES.map((lang) => (
                  <MenuItem key={lang} value={lang}>
                    {lang}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="body2" color="textSecondary">
              {currentLanguage === "English"
                ? "Rephrase in English"
                : `Translate to ${currentLanguage}`}
            </Typography>

            {isPromptModified && (
              <Chip
                label="Custom Prompt"
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
          </Box>

          <Button
            size="small"
            variant="text"
            onClick={() => setPromptExpanded(!promptExpanded)}
            endIcon={promptExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          >
            {promptExpanded ? "Hide Prompt" : "View/Edit Prompt"}
          </Button>
        </Box>

        <Collapse in={promptExpanded}>
          <Box mt={2}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
            >
              <Typography variant="subtitle2" color="textSecondary">
                AI System Prompt (for this video)
              </Typography>
              <Box display="flex" gap={1}>
                {isPromptModified && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleResetPrompt(currentVideo.unique_key)}
                  >
                    Reset to Default
                  </Button>
                )}
              </Box>
            </Box>
            <TextField
              fullWidth
              multiline
              rows={8}
              value={currentPrompt}
              onChange={(e) =>
                handlePromptChange(currentVideo.unique_key, e.target.value)
              }
              size="small"
              sx={{
                "& .MuiInputBase-root": {
                  fontFamily: "monospace",
                  fontSize: "0.85rem",
                },
              }}
            />
            <Typography variant="caption" color="textSecondary" mt={1}>
              This prompt instructs the AI how to rephrase/translate your content. Changes apply only to this video.
            </Typography>
          </Box>
        </Collapse>
      </Paper>

      <Grid container spacing={3}>
        {/* Original Content */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Original Content
                </Typography>
                <Tooltip title="Use original content">
                  <IconButton size="small" onClick={handleUseOriginal}>
                    <CompareArrowsIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box mb={2}>
                <Typography variant="caption" color="textSecondary">
                  Title
                </Typography>
                <Typography variant="body2">{state.original.title}</Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="caption" color="textSecondary">
                  Description
                </Typography>
                <Typography variant="body2">
                  {state.original.description || "No description"}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary">
                  Keywords
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                  {state.original.keywords.length > 0 ? (
                    state.original.keywords.map((kw, idx) => (
                      <Chip
                        key={idx}
                        label={kw}
                        size="small"
                        variant="outlined"
                      />
                    ))
                  ) : (
                    <Typography variant="caption">No keywords</Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Rephrased/Editable Content */}
        <Grid item xs={12} md={6}>
          <Card
            variant="outlined"
            sx={{ bgcolor: "primary.50", borderColor: "primary.main" }}
          >
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <AutoAwesomeIcon color="primary" />
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    color="primary"
                  >
                    {currentLanguage === "English"
                      ? "AI & SEO Content"
                      : `AI & SEO Translated Content (${currentLanguage})`}
                  </Typography>
                </Box>
                <Tooltip title="Rephrase again with current prompt & language">
                  <IconButton
                    size="small"
                    onClick={() =>
                      requestRephrase(
                        currentVideo.unique_key,
                        null,
                        currentPrompt,
                        currentLanguage
                      )
                    }
                    disabled={state.isLoading}
                  >
                    {state.isLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <RefreshIcon />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>

              <Box mb={2}>
                <TextField
                  fullWidth
                  label="Title"
                  value={state.editing.title}
                  onChange={(e) => handleEditChange("title", e.target.value)}
                  disabled={state.isLoading}
                  size="small"
                  InputProps={{
                    endAdornment: <EditIcon fontSize="small" color="action" />,
                  }}
                />
              </Box>

              <Box mb={2}>
                <TextField
                  fullWidth
                  label="Description"
                  value={state.editing.description}
                  onChange={(e) =>
                    handleEditChange("description", e.target.value)
                  }
                  disabled={state.isLoading}
                  multiline
                  rows={4}
                  size="small"
                  InputProps={{
                    endAdornment: <EditIcon fontSize="small" color="action" />,
                  }}
                />
              </Box>

              <Box>
                <TextField
                  fullWidth
                  label="Keywords (comma-separated)"
                  value={
                    Array.isArray(state.editing.keywords)
                      ? state.editing.keywords.join(", ")
                      : state.editing.keywords
                  }
                  onChange={(e) => {
                    const keywords = e.target.value
                      .split(",")
                      .map((k) => k.trim())
                      .filter((k) => k);
                    handleEditChange("keywords", keywords);
                  }}
                  disabled={state.isLoading}
                  size="small"
                  InputProps={{
                    endAdornment: <EditIcon fontSize="small" color="action" />,
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Action Buttons */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            onClick={handlePreviousVideo}
            disabled={currentVideoIndex === 0}
          >
            Previous Video
          </Button>
          {currentVideoIndex === 0 && (
            <Button variant="text" onClick={onBack}>
              ← Back to Source Selection
            </Button>
          )}
        </Box>

        <Box display="flex" gap={1}>
          {selectedVideos.map((_, idx) => (
            <Box
              key={idx}
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor:
                  idx < currentVideoIndex
                    ? "success.main"
                    : idx === currentVideoIndex
                    ? "primary.main"
                    : "grey.300",
              }}
            />
          ))}
        </Box>

        <Button
          variant="contained"
          color="primary"
          onClick={handleApprove}
          startIcon={<CheckCircleIcon />}
          disabled={state.isLoading || !state.editing.title}
        >
          {currentVideoIndex < selectedVideos.length - 1
            ? "Next Video"
            : "Approve All & Continue"}
        </Button>
      </Box>
    </Box>
  );
}

export default RephrasedContentReview;
